/**
 * The set of reviewer intents the cockpit keyboard model can produce. Each
 * maps to a queue/action handler in `useCockpitKeyboard`.
 */
export type CockpitIntent = 'next' | 'prev' | 'accept' | 'edit' | 'reject' | 'skip';

/**
 * Context for resolving a key press into a cockpit intent.
 *
 * `isTextInputFocused` is true when an editable element (the response editor,
 * a search box) has focus, in which case shortcuts must NOT fire so the user
 * can type freely.
 */
export interface CockpitKeyContext {
  key: string;
  isTextInputFocused: boolean;
}

const LETTER_INTENTS: Record<string, CockpitIntent> = {
  j: 'next',
  k: 'prev',
  a: 'accept',
  e: 'edit',
  r: 'reject',
  s: 'skip',
};

/**
 * Resolves a key press into a cockpit intent, or `null` when the key is
 * unmapped or a text input is focused.
 *
 * Letter shortcuts are case-insensitive; `ArrowDown`/`ArrowUp` and `Enter`
 * are matched verbatim.
 */
export function resolveCockpitKey({ key, isTextInputFocused }: CockpitKeyContext): CockpitIntent | null {
  if (isTextInputFocused) {
    return null;
  }
  if (key === 'ArrowDown') return 'next';
  if (key === 'ArrowUp') return 'prev';
  if (key === 'Enter') return 'accept';
  return LETTER_INTENTS[key.toLowerCase()] ?? null;
}
